import { Box, Stack, Theme, Typography, useTheme } from "@mui/material";
import type { TFunction } from "i18next";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  BricksIcon,
  CommercialItemIcon,
  EnergyClassIcon,
  EquipmentIcon,
  GarageIcon,
  HelmetIcon,
  InfoIcon,
  ParkingIcon,
  PlotIcon,
  RoomsIcon,
  StarOutlineIcon,
} from "@/assets";
import { getProsaWebBuildingStandardLabel } from "@/helpers/prosaWeb";
import { getConstructionTranslation } from "@/helpers/realEstateParametersHelpers";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { Construction, EnergyCertificateEnum, ObjectTypeEnum } from "@/types/refineRealEstate";

type ObjectDetailsState = CurrentValuationState | ValuationReportState;

interface ObjectDetailsProps {
  valuationState: ObjectDetailsState;
  isValuationPackageWidget?: boolean;
}

interface OverviewItem {
  id: string;
  dataCy?: string;
  label: string;
  value?: ReactNode;
  icon: ReactNode;
}

const visibleOverviewItemIdsByObjectType: Record<ObjectTypeEnum, string[]> = {
  [ObjectTypeEnum.house]: [
    "living-area",
    "plot-area",
    "construction-year",
    "energy-efficiency-class",
    "garage-count",
    "construction-type",
    "building-standard",
  ],
  [ObjectTypeEnum.apartment]: [
    "living-area",
    "construction-year",
    "rooms",
    "energy-efficiency-class",
    "garage-count",
    "parking-spaces",
    "building-standard",
  ],
  [ObjectTypeEnum.multiFamilyHouse]: [
    "living-area",
    "plot-area",
    "construction-year",
    "energy-efficiency-class",
    "garage-count",
    "construction-type",
    "building-standard",
  ],
  [ObjectTypeEnum.mixedUseProperty]: [
    "commercial-area",
    "living-area",
    "plot-area",
    "construction-year",
    "energy-efficiency-class",
    "garage-count",
    "construction-type",
    "building-standard",
  ],
  [ObjectTypeEnum.plotOfLand]: ["plot-area"],
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  root: {
    display: "grid",
    gap: theme.analysisLayout.sectionGap,
    width: "100%",
  },
  title: {
    color: theme.semanticColors.text.secondary,
    fontSize: theme.analysisLayout.bodyFontSize,
    lineHeight: "27px",
    fontWeight: 700,
  },
  metricsGrid: {
    display: "grid",
    gap: theme.analysisLayout.compactGap,
  },
  metricsRow: {
    display: "grid",
    alignItems: "center",
    columnGap: "40px",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
  },
  metricItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minHeight: "24px",
    minWidth: 0,
  },
  icon: {
    width: "28px",
    minWidth: "28px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.semanticColors.text.primary,
  },
  textRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
    minWidth: 0,
  },
});

const MetricItem = ({ item }: { item: OverviewItem }): JSX.Element => {
  const styles = createStyles(useTheme());
  return (
    <Box sx={styles.metricItem} data-cy={item.dataCy}>
      <Box sx={styles.icon}>{item.icon}</Box>
      <Box sx={styles.textRow}>
        <Typography component="span">{item.label}</Typography>
        {item.value !== undefined && item.value !== null && item.value !== "" && (
          <>
            <Typography component="span" sx={{ fontWeight: "bold" }}>
              ·
            </Typography>
            <Typography component="span" sx={{ fontWeight: "bold" }} data-cy={`${item.dataCy}-value`}>
              {item.value}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const formatNumber = (value?: number | null, unit?: string): string | undefined => {
  if (value === undefined || value === null || value === 0) return "-";
  return `${value.toLocaleString("de-DE")}${unit ? ` ${unit}` : ""}`;
};

const displayValue = (value: unknown): ReactNode => {
  if (value === undefined || value === null || value === 0 || value === "") {
    return "-";
  }

  return value as ReactNode;
};

const energyCertificateTranslationMap: Record<EnergyCertificateEnum, string> = {
  [EnergyCertificateEnum.A_PLUS]: "energyClass.values.A_PLUS",
  [EnergyCertificateEnum.A]: "energyClass.values.A",
  [EnergyCertificateEnum.B]: "energyClass.values.B",
  [EnergyCertificateEnum.C]: "energyClass.values.C",
  [EnergyCertificateEnum.D]: "energyClass.values.D",
  [EnergyCertificateEnum.E]: "energyClass.values.E",
  [EnergyCertificateEnum.F]: "energyClass.values.F",
  [EnergyCertificateEnum.G]: "energyClass.values.G",
  [EnergyCertificateEnum.H]: "energyClass.values.H",
};

const getTranslatedRealEstateValue = (
  value: unknown,
  translationGroup: "energyClass" | "construction",
  t: TFunction,
): ReactNode => {
  if (value === undefined || value === null || value === 0 || value === "") {
    return "-";
  }

  if (translationGroup === "construction") {
    return displayValue(getConstructionTranslation(value as Construction, t));
  }

  return displayValue(t(energyCertificateTranslationMap[value as EnergyCertificateEnum], { defaultValue: "-" }));
};

export const ObjectDetails = ({
  valuationState,
  isValuationPackageWidget = false,
}: ObjectDetailsProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);

  const { t } = useTranslation("PropertyDetailsPanel");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });

  const objectType = valuationState.propertyInformation?.generalInfo.objectType;
  const prosaWebBuildingStandardValue = valuationState.prosaWeb?.factors?.building_standard?.value;
  const buildingStandardLabel = getProsaWebBuildingStandardLabel(prosaWebBuildingStandardValue, tRealEstate);

  const items: OverviewItem[] = [
    {
      id: "commercial-area",
      dataCy: "property-details-overview-commercial-area",
      label: tRealEstate("commercialArea"),
      value: formatNumber(valuationState.propertyInformation?.buildingData?.commercialArea, "m²"),
      icon: <CommercialItemIcon />,
    },
    {
      id: "living-area",
      dataCy: "property-details-overview-living-area",
      label: tRealEstate("livingArea"),
      value: formatNumber(valuationState.propertyInformation?.buildingData?.livingArea, "m²"),
      icon: <EquipmentIcon />,
    },
    {
      id: "plot-area",
      dataCy: "property-details-overview-plot-area",
      label: tRealEstate("plot"),
      value: formatNumber(valuationState.propertyInformation?.plotData?.plotArea, "m²"),
      icon: <PlotIcon />,
    },
    {
      id: "construction-year",
      dataCy: "property-details-overview-construction-year",
      label: tRealEstate("constructionYear"),
      value: displayValue(valuationState.propertyInformation?.buildingData?.constructionYear),
      icon: <HelmetIcon />,
    },
    {
      id: "rooms",
      dataCy: "property-details-overview-rooms",
      label: tRealEstate("roomsNumber"),
      value: displayValue(valuationState.propertyInformation?.buildingData?.rooms),
      icon: <RoomsIcon />,
    },
    {
      id: "energy-efficiency-class",
      dataCy: "property-details-overview-energy-efficiency-class",
      label: tRealEstate("energyEfficiencyClass"),
      value: getTranslatedRealEstateValue(
        valuationState.propertyInformation?.energyAndPhotovoltaic?.energyCertificate,
        "energyClass",
        tRealEstate,
      ),
      icon: <EnergyClassIcon />,
    },
    {
      id: "garage-count",
      dataCy: "property-details-overview-garage-count",
      label: tRealEstate("garageCount"),
      value: displayValue(valuationState.propertyInformation?.buildingData?.garages),
      icon: <GarageIcon />,
    },
    {
      id: "parking-spaces",
      dataCy: "property-details-overview-parking-spaces",
      label: tRealEstate("parkingSpaces"),
      value: displayValue(valuationState.propertyInformation?.buildingData?.outdoorParkingSpaces),
      icon: <ParkingIcon />,
    },
    {
      id: "construction-type",
      dataCy: "property-details-overview-construction-type",
      label: tRealEstate("constructionType"),
      value: getTranslatedRealEstateValue(
        valuationState.propertyInformation?.buildingData?.construction,
        "construction",
        tRealEstate,
      ),
      icon: <BricksIcon />,
    },
    {
      id: "building-standard",
      label: tRealEstate("buildingStandard"),
      value: buildingStandardLabel,
      icon: <StarOutlineIcon />,
    },
  ].filter(
    (item) =>
      (isValuationPackageWidget || item.id !== "building-standard") &&
      (objectType === undefined || visibleOverviewItemIdsByObjectType[objectType].includes(item.id)),
  );
  const rows = Array.from({ length: Math.ceil(items.length / 3) }, (_, rowIndex) =>
    items.slice(rowIndex * 3, rowIndex * 3 + 3),
  );

  return (
    <Box sx={styles.root}>
      <Stack spacing={1}>
        <Typography sx={styles.title}>{t("overview")}</Typography>
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <InfoIcon sx={{ fontSize: theme.analysisLayout.titleFontSize }} />
          <Typography sx={{ fontSize: theme.analysisLayout.bodyFontSize }}>{t("propertyOverviewHint")}</Typography>
        </Stack>
      </Stack>

      <Box sx={styles.metricsGrid}>
        {rows.map((row, rowIndex) => (
          <Box key={`row-${rowIndex}`} sx={styles.metricsRow}>
            {row.map((item) => (
              <MetricItem item={item} key={item.id} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
